<script lang="ts">
  import { candidates, currentMode, trips, enabledAmenityKeys } from '../lib/state';
  import { setActiveCandidate } from '../lib/entities';
  import {
    overallScore, summarizeCandidate, scoreClass, timeClass, tripTimeClass,
    activeAmenities, COMMUTE_WEIGHT, AMENITY_WEIGHT, TRIP_WEIGHT,
  } from '../lib/scoring';
  import type { Candidate, TravelMode } from '../lib/types';
  const GAP = 12;
  const clampPct = (s: number) => Math.max(0, Math.min(100, s));

  interface DetailItem { label: string; value: string; cls: string }
  interface Section { label: string; score: number | null; items: DetailItem[] }
  interface Row {
    c: Candidate; total: number; commute: number; amenity: number | null; trip: number | null;
    modeAvgs: Partial<Record<TravelMode, number>>; sections: Section[];
  }

  function buildSections(c: Candidate, modeAvgs: Partial<Record<TravelMode, number>>, commute: number, amenity: number | null, enabled: Set<string>): Section[] {
    const am = c.amenities || {};
    const commuteItems: DetailItem[] = (['WALKING', 'BICYCLING', 'TRANSIT', 'DRIVING'] as TravelMode[]).map((mode) => {
      const v = modeAvgs[mode];
      return { label: mode.toLowerCase().slice(0, mode === 'BICYCLING' ? 4 : 5), value: v != null ? `${v}m` : '—', cls: v != null ? timeClass(v, mode) : 'muted' };
    });
    const amenityItems: DetailItem[] = activeAmenities(enabled).map((a) => {
      const data = am[a.key];
      const label = a.short || a.label.toLowerCase();
      if (!data || data.error || data.minutes == null) return { label, value: '—', cls: 'muted' };
      const cls = data.minutes <= a.good ? 'good' : data.minutes <= a.mid ? 'mid' : 'bad';
      return { label, value: `${data.minutes}m`, cls };
    });
    return [
      { label: 'Friend access', score: commute, items: commuteItems },
      { label: 'Daily places', score: amenity, items: amenityItems },
    ];
  }

  $: rows = ($candidates
    .map((c) => {
      const o = overallScore(c, $trips, $enabledAmenityKeys);
      if (!o) return null;
      return { c, ...o, sections: buildSections(c, o.modeAvgs, o.commute, o.amenity, $enabledAmenityKeys) } as Row;
    })
    .filter(Boolean) as Row[])
    .sort((a, b) => b.total - a.total);

  $: isClearWinner = rows.length >= 2 && rows[0].total - rows[1].total >= GAP;
  $: isClearLoser = rows.length >= 3 && rows[rows.length - 2].total - rows[rows.length - 1].total >= GAP;

  // Mode comparison table (sorted by avg for the current mode).
  $: compareRows = $candidates
    .map((c) => ({ name: c.name, s: summarizeCandidate(c, $currentMode) }))
    .filter((r) => r.s != null)
    .sort((a, b) => (a.s!.avg - b.s!.avg));

  const pct = (n: number) => Math.round(n * 100);
</script>

{#if rows.length >= 2}
  <div class="section-title" style="margin-top: 24px;">
    Why these scores <span class="meta">{pct(COMMUTE_WEIGHT)}/{pct(AMENITY_WEIGHT)}/{pct(TRIP_WEIGHT)} blend</span>
  </div>
  <p class="ranking-note">
    <strong>NestScore</strong> blends friend access ({pct(COMMUTE_WEIGHT)}%), daily places ({pct(AMENITY_WEIGHT)}%)
    and weekend places ({pct(TRIP_WEIGHT)}%). Click any card for full details.
  </p>

  {#each rows as r, i (r.c.id)}
    {@const top = i === 0 && isClearWinner}
    {@const bot = i === rows.length - 1 && isClearLoser}
    <div
      class="rank-card {top ? 'winner' : bot ? 'loser' : ''}"
      role="button"
      tabindex="0"
      aria-label={`View ${r.c.name} details`}
      on:click={() => setActiveCandidate(r.c.id)}
      on:keydown={(e) => e.key === 'Enter' && setActiveCandidate(r.c.id)}
    >
      <div class="rank-card-header">
        <div class="rank-card-rank">#{i + 1}</div>
        <div class="rank-card-title">
          <span class="candidate-color-dot" style={`background:${r.c.color}; width:14px; height:14px;`}></span>
          <span class="rank-card-name">{r.c.name}</span>
          {#if top}<span class="ranking-badge winner">clear winner</span>{/if}
          {#if bot}<span class="ranking-badge loser">slowest</span>{/if}
        </div>
        <div class="rank-card-total">
          <div class="rank-card-total-num {scoreClass(r.total)}">{r.total}</div>
          <div class="rank-card-total-label">NestScore</div>
        </div>
      </div>

      {#each r.sections as s}
        {@const cls = s.score != null ? scoreClass(s.score) : 'muted'}
        <div class="rank-card-section">
          <div class="rank-card-section-header">
            <span class="rank-card-section-label">{s.label}</span>
            <span class="rank-card-section-score {cls}">{s.score ?? '—'}<span class="of"> / 100</span></span>
          </div>
          <div class="rank-card-bar"><div class="rank-card-bar-fill {cls}" style={`width:${s.score != null ? clampPct(s.score) : 0}%`}></div></div>
          <div class="rank-card-detail">
            {#each s.items as it}
              <div class="rank-card-detail-item">
                <div class="rank-card-detail-label" title={it.label}>{it.label}</div>
                <div class="rank-card-detail-value {it.cls}">{it.value}</div>
              </div>
            {/each}
          </div>
        </div>
      {/each}

      {#if $trips.length}
        {@const tcls = r.trip != null ? scoreClass(r.trip) : 'muted'}
        <div class="rank-card-section">
          <div class="rank-card-section-header">
          <span class="rank-card-section-label">Places</span>
            <span class="rank-card-section-score {tcls}">{r.trip ?? '—'}<span class="of"> / 100</span></span>
          </div>
          <div class="rank-card-bar"><div class="rank-card-bar-fill {tcls}" style={`width:${r.trip != null ? clampPct(r.trip) : 0}%`}></div></div>
          <table class="rank-card-trip-table">
            <thead><tr><th>places</th><th>drive</th><th>transit</th></tr></thead>
            <tbody>
              {#each $trips as t}
                {@const d = r.c.trips[t.id] || {}}
                <tr>
                  <td>{t.name}</td>
                  <td class="time {d.driving?.minutes != null ? tripTimeClass(d.driving.minutes) : 'muted'}">{d.driving?.minutes != null ? `${d.driving.minutes}m` : '—'}</td>
                  <td class="time {d.transit?.minutes != null ? tripTimeClass(d.transit.minutes) : 'muted'}">{d.transit?.minutes != null ? `${d.transit.minutes}m` : '—'}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </div>
  {/each}
{/if}

{#if compareRows.length >= 2}
  <div class="section-title" style="margin-top: 20px;">Compare candidates ({$currentMode.toLowerCase()})</div>
  <table class="results-table">
    <thead><tr><th>Candidate</th><th style="text-align:right">Avg</th><th style="text-align:right">Worst</th></tr></thead>
    <tbody>
      {#each compareRows as r}
        <tr>
          <td>{r.name}</td>
          <td class="time-cell {r.s!.avgClass}">{r.s!.avg} min</td>
          <td class="time-cell {r.s!.worstClass}">{r.s!.worst} min</td>
        </tr>
      {/each}
    </tbody>
  </table>
{/if}
